import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { useParams, useSearchParams } from "react-router-dom";
import { useTran } from "@/hooks";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormField } from "@/components/common";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuthStore } from "@/features/auth";
import { useSurveyTakePageStore } from "../hooks";
import SurveyTake from "../components/SurveyTake";

const SurveyTakePage = () => {
  const { t } = useTran();
  const { surveyType, surveyId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuthStore();
  const takePageStore = useSurveyTakePageStore();

  const token = searchParams.get("token") || "";
  const inviteEmail = (searchParams.get("email") || "").trim();
  const inviteName = (searchParams.get("name") || "").trim();
  const inviteOrg = (searchParams.get("org") || "").trim();

  const isAuthenticated = Boolean(user);

  useEffect(() => {
    takePageStore.setParams({
      surveyType,
      surveyId,
      token,
      inviteEmail,
      inviteName,
      inviteOrg,
      isAuthenticated,
    });
    takePageStore.loadSurvey(t);
  }, [
    inviteEmail,
    inviteName,
    inviteOrg,
    isAuthenticated,
    surveyId,
    surveyType,
    t,
    takePageStore,
    token,
  ]);

  if (takePageStore.error) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>{t("Survey link unavailable")}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {takePageStore.error}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {takePageStore.loading ? <Skeleton className="h-32 w-full" /> : null}

      {!isAuthenticated ? (
        <Card>
          <CardHeader>
            <CardTitle>{t("Tell us about you")}</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4 md:grid-cols-3">
            <FormField
              label={t("Full name")}
              value={takePageStore.guestInfo.name}
              onChange={(event) =>
                takePageStore.setGuestField("name", event.target.value)
              }
              placeholder={t("Enter full name")}
            />
            <FormField
              label={t("Email")}
              type="email"
              value={takePageStore.guestInfo.email}
              onChange={(event) =>
                takePageStore.setGuestField("email", event.target.value)
              }
              placeholder={t("Enter email")}
            />
            <FormField
              label={t("Organization")}
              value={takePageStore.guestInfo.organization}
              onChange={(event) =>
                takePageStore.setGuestField("organization", event.target.value)
              }
              placeholder={t("Enter organization")}
            />
            {!takePageStore.guestInfoValid ? (
              <p className="text-xs text-destructive md:col-span-3">
                {t("Please complete your details before submitting.")}
              </p>
            ) : null}
          </CardContent>
        </Card>
      ) : null}

      {!takePageStore.loading ? (
        <SurveyTake
          survey={takePageStore.survey}
          shareToken={token}
          externalRespondent={
            isAuthenticated
              ? undefined
              : takePageStore.guestInfoValid
                ? takePageStore.guestInfo
                : undefined
          }
          disableSubmit={!takePageStore.guestInfoValid}
        />
      ) : null}
    </div>
  );
};

export default observer(SurveyTakePage);
